import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import GroupWorkIcon from "@mui/icons-material/GroupWork";
import TuneIcon from "@mui/icons-material/Tune";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import FeederGroupManager from "./FeederGroupManager";
import ConfigurationManager from "./ConfigurationManager";
import ScheduleLS from "../LoadShiftSchedule/ScheduleLoadShift";
import SchedulePS from "../SchedulePeakShaving/SchedulePeakShaving";
import PublicHoliday from "../Financial/PublicHoliday";
import TOU from "../Financial/TOU";
import Tariff from "../Financial/Tariff";
import ResetGeysers from "./ResetGeysers";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary:   { main: "#FDB940" },
    secondary: { main: "#93C5FD" },
    error:     { main: "#f87171" },
    success:   { main: "#4ade80" },
    background: {
      default: "#0d1117",
      paper:   "#1C232C",
    },
    text: {
      primary:   "#e0e6f0",
      secondary: "rgba(255,255,255,0.5)",
    },
    divider: "rgba(255,255,255,0.08)",
  },
  shape: { borderRadius: 8 },
  components: {
    MuiPaper:     { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiAccordion: { styleOverrides: { root: { backgroundImage: "none", backgroundColor: "#1C232C", "&:before": { display: "none" } } } },
    MuiOutlinedInput: { styleOverrides: { notchedOutline: { borderColor: "rgba(255,255,255,0.15)" } } },
    MuiChip: { styleOverrides: { root: { borderColor: "rgba(255,255,255,0.15)" } } },
    MuiTypography: { styleOverrides: { root: { color: "#e0e6f0" } } },
    MuiListItemText: { styleOverrides: { primary: { color: "#e0e6f0" }, secondary: { color: "rgba(255,255,255,0.5)" } } },
    MuiInputLabel: { styleOverrides: { root: { color: "rgba(255,255,255,0.5)" } } },
    MuiSvgIcon:    { styleOverrides: { root: { color: "rgba(255,255,255,0.6)" } } },
  },
});

const TAB_SX = {
  "& .MuiTab-root": {
    textTransform: "none",
    fontSize: "0.88rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.55)",
    minHeight: 44,
    gap: "6px",
    px: "16px",
  },
  "& .Mui-selected": {
    color: "#FDB940 !important",
    fontWeight: 600,
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#FDB940",
    height: 2,
  },
  "& .MuiTab-iconWrapper": {
    color: "inherit",
  },
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

export default function OperatorSettingsPage() {
  const [selected, setSelected] = useState(0);

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ maxWidth: 1500, margin: "0 auto", padding: "80px 32px 48px", minHeight: "100vh", color: "#e0e6f0" }}>

        {/* Page header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "24px" }}>
          <Box>
            <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.025em" }}>
              Settings
            </Typography>
            <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.45)", mt: "4px" }}>
              Feeder groups · configurations · schedules · financial — all changes apply to the live DSM programme
            </Typography>
          </Box>
          <ResetGeysers
            label="Emergency intervention"
            buttonIcon={<WarningAmberIcon sx={{ fontSize: "1rem", color: "#f87171" }} />}
          />
        </Box>

        {/* Horizontal tabs */}
        <Tabs
          value={selected}
          onChange={(_, v) => setSelected(v)}
          sx={TAB_SX}
        >
          <Tab icon={<GroupWorkIcon sx={{ fontSize: "1rem" }} />} iconPosition="start" label="Feeder Groups" />
          <Tab icon={<TuneIcon sx={{ fontSize: "1rem" }} />} iconPosition="start" label="Configurations" />
          <Tab icon={<CalendarTodayIcon sx={{ fontSize: "1rem" }} />} iconPosition="start" label="Load Shifting" />
          <Tab icon={<GpsFixedIcon sx={{ fontSize: "1rem" }} />} iconPosition="start" label="Peak Shaving" />
          <Tab icon={<AccountBalanceIcon sx={{ fontSize: "1rem" }} />} iconPosition="start" label="Financial" />
        </Tabs>

        {/* Content */}
        <Box sx={{ pt: "28px" }}>
          {selected === 0 && <FeederGroupManager />}
          {selected === 1 && <ConfigurationManager />}
          {selected === 2 && <ScheduleLS />}
          {selected === 3 && <SchedulePS />}
          {selected === 4 && (
            <Box>
              <Box sx={{ mb: "20px" }}>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
                  Financial
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.4)", mt: "3px" }}>
                  Public holidays · Time-of-use bands · Tariff rates
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <PublicHoliday />
                <TOU />
                <Tariff />
              </Box>
            </Box>
          )}
        </Box>

      </Box>
    </ThemeProvider>
  );
}
